import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, Users, IndianRupee, Star, Save } from "lucide-react";
import { api, formatApiError, formatINR, CITIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SPECIALTIES = ["Cardiology","Dermatology","Pediatrics","Orthopedics","Neurology","Gynecology","General Physician","ENT","Ophthalmology","Dentistry"];

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("today");
  const [profile, setProfile] = useState(null);
  const [appts, setAppts] = useState([]);

  const [form, setForm] = useState({
    specialty: "", qualification: "", registration_no: "", experience_years: 0,
    gender: "male", city: "", clinic_name: "", clinic_address: "",
    consultation_fee: 500, slot_duration_min: 30, bio: "", languages: ["English"],
    working_hours: { start: "09:00", end: "17:00" },
  });

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/doctors/me"), api.get("/appointments/my")]);
    if (p.data?.id) {
      setProfile(p.data);
      setForm({ ...form, ...p.data });
    }
    setAppts(a.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.post("/doctors/profile", {
        ...form,
        experience_years: Number(form.experience_years),
        consultation_fee: Number(form.consultation_fee),
        slot_duration_min: Number(form.slot_duration_min),
      });
      toast.success(profile ? "Profile updated" : "Profile submitted for admin approval");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, null, { params: { status } });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const todays = appts.filter((a) => a.date === today);
  const upcoming = appts.filter((a) => a.date > today && ["confirmed", "pending"].includes(a.status));
  const earnings = appts.filter((a) => a.status === "completed").reduce((s, a) => s + (a.fee || 0), 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Doctor Panel</h1>
          <p className="mt-1 text-sm text-slate-600">Welcome, {user?.name}</p>
        </div>
        {profile && <span className={`badge border ${profile.status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : profile.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-rose-50 text-rose-800 border-rose-200"}`}>
          Status: {profile.status}
        </span>}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { l: "Today's Appointments", v: todays.length, i: Calendar },
          { l: "Upcoming", v: upcoming.length, i: Clock },
          { l: "Total Patients", v: new Set(appts.map((a) => a.patient_user_id)).size, i: Users },
          { l: "Earnings", v: formatINR(earnings), i: IndianRupee },
        ].map((s) => (
          <div key={s.l} className="card p-5">
            <s.i className="h-5 w-5 text-teal-600" />
            <div className="mt-3 text-2xl font-extrabold text-slate-900">{s.v}</div>
            <div className="text-xs font-medium text-slate-500">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200">
        {[{ id: "today", label: "Today" }, { id: "all", label: "All Appointments" }, { id: "profile", label: profile ? "Edit Profile" : "Complete Profile" }].map((t) => (
          <button key={t.id} data-testid={`doc-tab-${t.id}`} onClick={() => setTab(t.id)} className={`relative px-4 py-3 text-sm font-semibold ${tab === t.id ? "text-blue-700" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
            {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-700" />}
          </button>
        ))}
      </div>

      {(tab === "today" || tab === "all") && (
        <div className="mt-6 space-y-3">
          {(tab === "today" ? todays : appts).map((a) => (
            <div key={a.id} data-testid={`doc-appt-${a.booking_id}`} className="card flex flex-wrap items-center justify-between gap-3 p-5">
              <div>
                <div className="font-mono text-[11px] text-slate-500">#{a.booking_id}</div>
                <div className="mt-0.5 font-bold text-slate-900">{a.patient_name} <span className="text-xs font-medium text-slate-500">• {a.patient_age || "?"} yrs</span></div>
                <div className="text-xs text-slate-500">{a.date} • {a.time} • {formatINR(a.fee)}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`badge border ${a.status === "confirmed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : a.status === "completed" ? "bg-blue-50 text-blue-800 border-blue-200" : a.status === "cancelled" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{a.status}</span>
                {a.status === "confirmed" && (
                  <>
                    <button data-testid={`complete-${a.booking_id}`} onClick={() => updateStatus(a.id, "completed")} className="btn-secondary !py-1.5 !text-xs">Mark Completed</button>
                    <button data-testid={`noshow-${a.booking_id}`} onClick={() => updateStatus(a.id, "no_show")} className="btn-outline !py-1.5 !text-xs">No-Show</button>
                  </>
                )}
              </div>
            </div>
          ))}
          {(tab === "today" ? todays : appts).length === 0 && <div className="card p-10 text-center text-sm text-slate-500">No appointments to show.</div>}
        </div>
      )}

      {tab === "profile" && (
        <form onSubmit={saveProfile} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <select data-testid="prof-specialty" required className="input-field" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}>
            <option value="">Select Specialty</option>
            {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input data-testid="prof-qual" required className="input-field" placeholder="Qualification (MBBS, MD...)" value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} />
          <input data-testid="prof-regno" required className="input-field" placeholder="Medical Registration No." value={form.registration_no} onChange={(e) => setForm({ ...form, registration_no: e.target.value })} />
          <input data-testid="prof-exp" required type="number" className="input-field" placeholder="Years of experience" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} />
          <select data-testid="prof-gender" className="input-field" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
            <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
          </select>
          <select data-testid="prof-city" required className="input-field" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}>
            <option value="">Select City</option>
            {CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="prof-clinic" required className="input-field" placeholder="Clinic / Hospital name" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} />
          <input data-testid="prof-fee" required type="number" className="input-field" placeholder="Consultation fee (₹)" value={form.consultation_fee} onChange={(e) => setForm({ ...form, consultation_fee: e.target.value })} />
          <input data-testid="prof-address" required className="input-field md:col-span-2" placeholder="Clinic address" value={form.clinic_address} onChange={(e) => setForm({ ...form, clinic_address: e.target.value })} />
          <div className="grid grid-cols-3 gap-3 md:col-span-2">
            <input data-testid="prof-start" type="time" className="input-field" value={form.working_hours?.start} onChange={(e) => setForm({ ...form, working_hours: { ...form.working_hours, start: e.target.value } })} />
            <input data-testid="prof-end" type="time" className="input-field" value={form.working_hours?.end} onChange={(e) => setForm({ ...form, working_hours: { ...form.working_hours, end: e.target.value } })} />
            <select data-testid="prof-duration" className="input-field" value={form.slot_duration_min} onChange={(e) => setForm({ ...form, slot_duration_min: e.target.value })}>
              <option value={15}>15 min slots</option><option value={20}>20 min slots</option><option value={30}>30 min slots</option><option value={45}>45 min slots</option>
            </select>
          </div>
          <textarea data-testid="prof-bio" rows={3} className="input-field md:col-span-2" placeholder="Short professional bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          <button data-testid="prof-save" className="btn-primary md:col-span-2"><Save className="h-4 w-4" /> {profile ? "Update Profile" : "Submit for Approval"}</button>
        </form>
      )}
    </div>
  );
}
